import React from 'react';

import { css } from '@emotion/css';
import { Icon, IconButton, InputAdornment, TextField, Theme, useTheme } from '@mui/material';
import { DateRangeFilter } from '@hopdrive/sdk-ui';
import type { DateRangeValue } from '@hopdrive/sdk-ui';

import ClearFiltersButton from '@components/Utils/ClearFiltersButton';

// TYPES ---------------------------------------------------------------------- //

interface TeamTabControlsProps {
  range: DateRangeValue;
  onRangeChange: (value: DateRangeValue) => void;
  search: string;
  onSearchChange: (search: string) => void;
  onClear: () => void;
}

// COMPONENT ------------------------------------------------------------------ //

/** No driver filter here. The map's own driver sidebar is the driver filter. */
const TeamTabControls = ({ range, onRangeChange, search, onSearchChange, onClear }: TeamTabControlsProps) => {
  const theme = useTheme();
  const cls = useStyles(theme);

  // Local input so typing is free; the committed search reaches the map only on Enter or clear.
  const [searchInput, setSearchInput] = React.useState<string>(search || '');
  React.useEffect(() => {
    setSearchInput(search || '');
  }, [search]);

  const handleSearchCommit = () => onSearchChange(searchInput.trim());
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') handleSearchCommit();
  };
  const handleClearSearch = () => {
    setSearchInput('');
    onSearchChange('');
  };
  // The local input only re-syncs when the committed `search` prop changes, so resetting it here
  // keeps a typed-but-not-entered term from lingering in the box.
  const handleClearAll = () => {
    setSearchInput('');
    onClear();
  };

  return (
    <div className={cls.header}>
      <div className={cls.controls}>
        <DateRangeFilter value={range} onChange={value => onRangeChange(value)} size='small' />

        <TextField
          label='Search (Press Enter)'
          placeholder='Search for moves...'
          variant='outlined'
          size='small'
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          onKeyDown={handleSearchKeyDown}
          sx={{ flex: 1 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position='start'>
                  <Icon color='disabled' fontSize='small'>
                    search
                  </Icon>
                </InputAdornment>
              ),
              endAdornment: searchInput ? (
                <InputAdornment position='end'>
                  {searchInput !== search ? (
                    <IconButton size='small' onClick={handleSearchCommit} title='Search'>
                      <Icon fontSize='small'>arrow_forward</Icon>
                    </IconButton>
                  ) : (
                    <IconButton size='small' onClick={handleClearSearch} title='Clear search'>
                      <Icon fontSize='small'>close</Icon>
                    </IconButton>
                  )}
                </InputAdornment>
              ) : null,
            },
          }}
        />

        <ClearFiltersButton onClear={handleClearAll} />
      </div>
    </div>
  );
};

// STYLES --------------------------------------------------------------------- //

const useStyles = (theme: Theme) => ({
  header: css`
    background-color: ${theme.palette.background.paper};
    border-bottom: 1px solid ${theme.palette.divider};
  `,
  controls: css`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: ${theme.spacing(2)};
  `,
});

// EXPORT --------------------------------------------------------------------- //

export default TeamTabControls;
